'use strict';
import { Injectable } from '@angular/core';

@Injectable()
export class ExternalSearchService {
  constructor() { }

  public getDefaultStatus(formConfigParams: any): { [key: string]: boolean} {
    const defaultParams: { [key: string]: boolean; } = { };
    formConfigParams['groups'].forEach(group => {
      if (group['fields']) {
        group['fields'].forEach(field =>
          defaultParams[field['name']] = field['default']);
      }
    });
    return defaultParams;
  }

  public getDefaultSearchQuery(searchURL: string, searchDataConfigParams: any, formConfigParams: any, data: { [key: string]: string; }): string {
    const defaultParams: { [key: string]: boolean; } = this.getDefaultStatus(formConfigParams);
    return this.getSearchQuery(searchURL, searchDataConfigParams, data, defaultParams);
  }

  public runDefaultSearchQuery(searchURL: string, searchDataConfigParams: any, formConfigParams: any, data: { [key: string]: string; }) {
    const searchQuery = this.getDefaultSearchQuery(searchURL, searchDataConfigParams, formConfigParams, data);
    if (searchQuery) {
      window.open(searchQuery, '_blank');
    }
    else {
      console.error('ExternalSearchService->runDefaultSearchQuery: no searchQuery was found');
    }
  }

  public getSearchQueryBySwitches(searchURL: string, searchDataConfigParams: any, data: { [key: string]: string; }, switchesStatus: any): string {
    return this.getSearchQuery(searchURL, searchDataConfigParams, data, switchesStatus);
  }

  public getSearchQueryByTemplate(searchURL: string, searchDataConfigParams: any, data: { [key: string]: string; }, inSearchTemplate: string): string {
    return this.getSearchQuery(searchURL, searchDataConfigParams, data, null, inSearchTemplate);
  }

  private getSearchQuery(searchURL: string, searchDataConfigParams: any, data: { [key: string]: string; },
                        switchesStatus: any = null, inSearchTemplate: string = ''): string {

    const mandatoryParams: { [key: string]: boolean; } = { };
    const constParamsNames: { [key: string]: string; } = { };
    const quotedParams: { [key: string]: boolean; } = { };
    const removePrefixParams: { [key: string]: string; } = { };
    const arrowFormatsParams: { [key: string]: string; } = { };
    const useMappingTableParams: { [key: string]: string; } = { }; // For data that has mapping table - for now it is relevant only to Amino acid which has one and three letter codes
    searchDataConfigParams['params'].forEach(param => {
      if (param['type'] === 'const') {
        // In case param can has value, using it. Else, using the param name. This allows to mpp a different param name
        constParamsNames[param['name']] = param['value'] ? param['value'] : param['name'];
      }
      mandatoryParams[param['name']] = param['hidden'];
      quotedParams[param['name']] = param['addQuotes'];
      removePrefixParams[param['name']] = param['removePrefix'];
      arrowFormatsParams[param['name']] = param['arrowFormats'];
      useMappingTableParams[param['name']] = param['useMappingTable'];
    });

   let searchTemplate: string = inSearchTemplate;
   if (switchesStatus) { // using default template when switches are used
     searchTemplate = searchDataConfigParams['searchTemplate'];
     if (!searchTemplate) {
       console.error('getDefaultStatus->getSearchQuery: cannot find searchTemplate');
       return '';
     }
   }
    const queryDynamicParams: string[] = [];
    const queryConstParams: string[] = [];
    this.parseSearchParams(searchTemplate, queryDynamicParams, queryConstParams);

    const fieldsList: { [key: string]: any; } = { }; // contains field and objects. Field is an object when it has 'relatedFields' (e.g. aberration in C-NOTATION)
    this.getFieldsValue(data, searchDataConfigParams, queryDynamicParams, fieldsList);
    const currentSearchTemplate = searchURL + this.buildQuery(searchTemplate, mandatoryParams, constParamsNames,
          quotedParams, removePrefixParams, arrowFormatsParams, fieldsList, queryDynamicParams, queryConstParams, useMappingTableParams, switchesStatus);
    return currentSearchTemplate;
  }

  private buildQuery(searchTemplate: string, mandatoryParams: { [key: string]: boolean; }, constParamsNames: { [key: string]: string; }, quotedParams: { [key: string]: boolean; },
                     removePrefixParams: { [key: string]: string; }, arrowFormatsParams: { [key: string]: string; }, fieldsList: { [key: string]: string; },
                     queryDynamicParams: string[], queryConstParams: string[], useMappingTableParams: { [key: string]: string; }, switchesStatus: any = null): string {
    let outTemplate: string = searchTemplate;
    for (const param of queryDynamicParams) {
      const paramFullName: string = '$(' + param + ')';
      if (fieldsList[param] && ((switchesStatus && switchesStatus[param]) || !switchesStatus || mandatoryParams[param])) {
        // remove prefix (eg. c.; p.)
        if (removePrefixParams[param]) {
          const prefixes: string [] = removePrefixParams[param].split(';'); // support multiple prefix delimited by ';'
          for (const prefix of prefixes) {
            if (typeof(fieldsList[param]) == 'object') { // In case field is an object, go over all its internal fields
              for (const field of Object.keys(fieldsList[param])) {
                if (fieldsList[param][field]) {
                  if (prefix.startsWith('*')) { // removing prefix until (e.g. if param = '*.g' then NC_000007.13:g.140453136A>T will be 140453136A>T )
                    const clearPrefix: string = prefix.replace('*', '');
                    const idx: number = fieldsList[param][field].toUpperCase().indexOf(clearPrefix.toUpperCase());
                    if (idx > -1) {
                      fieldsList[param][field] = fieldsList[param][field].substring(idx + clearPrefix.length, fieldsList[param][field].length);
                    }
                  }
                  else if (fieldsList[param][field].toUpperCase().startsWith(prefix.toUpperCase())) { // removing exact prefix
                    fieldsList[param][field] = fieldsList[param][field].substring(prefix.length, fieldsList[param][field].length);
                  }
                }
              }
            }
            else if (fieldsList[param].toUpperCase().startsWith(prefix.toUpperCase())) {
              fieldsList[param] = fieldsList[param].substring(prefix.length, fieldsList[param].length);
            }
          }
        }
        if (arrowFormatsParams[param]) {
          if (typeof(fieldsList[param]) == 'object') {
            for (const field of Object.keys(fieldsList[param])) {
              fieldsList[param][field] = this.addArrowFormats(fieldsList[param][field], arrowFormatsParams[param]);
            }
          }
          else {
            fieldsList[param] = this.addArrowFormats(fieldsList[param], arrowFormatsParams[param]);
          }
        }
        if (useMappingTableParams[param]) {
          fieldsList[param] = this.getMappedValues(fieldsList[param], useMappingTableParams[param]);
        }
        if (quotedParams[param]) {
          outTemplate = outTemplate.replace(paramFullName, '"' + fieldsList[param] + '"');
        }
        else {
          if (typeof(fieldsList[param]) == 'object') {
            let itemData: string = '';
            for (const key of Object.keys(fieldsList[param])) {
              const field = fieldsList[param][key];
              if (field != '') {
                itemData = itemData == '' ? itemData + field : itemData + ' | ' + field;
              }
            }
            outTemplate = outTemplate.replace(paramFullName, itemData);
          }
          else {
            outTemplate = outTemplate.replace(paramFullName, fieldsList[param]);
          }
        }
      }
      else {
        outTemplate = this.removeParameter(paramFullName, outTemplate);
      }
    }
    for (const param of queryConstParams) {
      const paramFullName: string = '$[' + param + ']';
      if ((switchesStatus && switchesStatus[param]) || !switchesStatus) {
        outTemplate = outTemplate.replace(paramFullName, constParamsNames[param]);
      }
      else {
        outTemplate = this.removeParameter(paramFullName, outTemplate);
      }
    }
    outTemplate = this.clearTemplate(outTemplate);
    return outTemplate.trim();
  }

  addArrowFormats(field: string, arrowFormats: string): string {
    if (!field) {
      return '';
    }
    // Adding all arrow formats, e.g. "407A>G" | "407A->G" | "407A-->G" | "407A/G"
    let str: string = field;
    let formatsArr = arrowFormats.split(';');

    // first, sort the array to prevent replacing '>' when the actual symbol is '-->'
    formatsArr = formatsArr.sort((a, b) => {
      if (a.length > b.length) {
        return 1;
      }
      if (a.length < b.length) {
        return -1;
      }
      return 0;
    });
    formatsArr = formatsArr.reverse();

    // looking for the existing arrow
    let currentArrowSymbol: string;
    for (const item of formatsArr) {
      if (field.indexOf(item) > 0) {
        currentArrowSymbol = item;
        break;
      }
    }

    // adding all arrows delimited by ' | '
    const outArrows: string [] = [];
    if (currentArrowSymbol) {
      for (const item of formatsArr) {
        outArrows.push(field.replace(currentArrowSymbol, item));
      }
      if (outArrows.length > 0) {
        str = outArrows.join(' | ');
      }
    }
    return str;
  }

  getMappedValues(parameter: any, mappedFeildName: string): string {
    // Add the mapped value to the value itself
    if (typeof(parameter) == 'object') {
      for (const field of Object.keys(parameter)) {
        if (field == mappedFeildName && parameter[field]) {
          if (mappedFeildName == 'AminoAcidChange') {
            parameter[field] = AminoAcidHandler.getAminoAcidValue(parameter[field]);
          }
          break;
        }
      }
    }
    else {
      if (parameter) {
        if (mappedFeildName == 'AminoAcidChange') {
          parameter = AminoAcidHandler.getAminoAcidValue(parameter);
        }
      }
    }
    return parameter;
  }

  private clearTemplate(template: string) {
    // Delete unneeded symbols
    template = template.replace(/\[\s*OR+/gi, '['); // remove [OR
    template = template.replace(/\[\s*\]/g, ''); // remove []
    template = template.replace(/\+\s*\+/g, '+'); // remove ++
    template = template.replace(/\[\s* +/g, '['); // remove [ +
    template = template.replace(/\s*\] +/g, ']'); // remove ] +
    template = template.replace(/\s*\+\s*/g, '+'); // remove spaces around +
    template = template.replace(/\s*\[\s*/g, '['); // remove spaces around [
    template = template.replace(/\s*\]\s*/g, ']'); // remove spaces around ]

    template = template.replace(/  +/g, ' ').trim();
    if (template.endsWith('+')) {
      template = template.substring(0, template.length - 1).trim();
    }
    if (template.startsWith('+')) {
      template = template.substring(1, template.length).trim();
    }
    return template;
  }

  private removeParameter(paramName: string , template: string): string {
    // Removing parameters without values and their operators from the query
    let outTemplate = this.removeOperator('+', paramName, template);
    outTemplate = this.removeOperator('OR', paramName, outTemplate);
    outTemplate = outTemplate.replace(paramName, '');
    return outTemplate.trim();
  }

  private getFieldsValue(data: { [key: string]: string; }, configParams: any,  queryDynamicParams: string[], fieldsList: { [key: string]: any; }) {
    // Adding fields that participate in the query and their values
    for (const param of queryDynamicParams) {
      for (const configParam of configParams['params']) {
        if (configParam.name === param) {
          if (configParam['mappedFields'].length > 0) {
            // One field is mapped
            if (configParam['mappedFields'].length == 1) {
              if (data[configParam.mappedFields[0]]) {
                if (configParam['relatedFields']) { // Related fields are similar to mapped fields BUT has logic - like arrow formats
                  fieldsList[configParam.name] = { };
                  fieldsList[configParam.name][configParam.mappedFields] = data[configParam.mappedFields[0]];
                  for (const relatedField of configParam['relatedFields']) {
                    fieldsList[configParam.name][relatedField] = data[relatedField];
                  }
                } else {
                  fieldsList[configParam.name] = data[configParam.mappedFields[0]];
                }
              }
            }
            else {
              // Multiple fields are mapped
              let firstMappedField: boolean = true;
              let combinedData: string = '';
              for (const mappedFieldElement of configParam['mappedFields']) {
                if (data[mappedFieldElement]) {
                  combinedData += firstMappedField ? data[mappedFieldElement] :
                    configParam['operator'] + data[mappedFieldElement];
                  firstMappedField = false;
                }
              }
              if (combinedData) {
                fieldsList[configParam.name] = combinedData;
              }
            }
          }
          break;
        }
      }
    }
  }

  private parseSearchParams(searchTemplate: string, queryDynamicParams: string[], queryConstParams: string[]) {
    // Reading search parameters from config
    if (searchTemplate) {
      const dynamicParams: string [] = searchTemplate.split('$('); // getting dynamic params
      for (let i = 1; i < dynamicParams.length; i++) { // Starts with 1 becaise first parameter is empty\unneeded string
        const pos = dynamicParams[i].indexOf(')');
        if (pos > 0) {
          queryDynamicParams.push(dynamicParams[i].substring(0, pos));
        }
      }
      const staticParams: string [] = searchTemplate.split('$['); // getting static params
      for (let i = 1; i < staticParams.length; i++) {
        const pos = staticParams[i].indexOf(']');
        if (pos > 0) {
          queryConstParams.push(staticParams[i].substring(0, pos));
        }
      }
    }
    else {
      console.error('ExternalSearchService->parseSearchParams: no searchTemplate was found');
    }
  }

  private removeOperator(operator: string, parameterName: string, searchTemplate: string): string {
    let outputStr: string = searchTemplate;
    const parsedStr: string[] = outputStr.split(parameterName);
    if (parsedStr.length == 2) {
      const prefix: string = parsedStr[0].trim();
      if (prefix.endsWith(operator)) {
        outputStr = prefix.substring(0, prefix.length - operator.length).trim() + ' ' + parameterName + parsedStr[1].trim();
      }
    }
    return outputStr.trim();
  }
}


export class AminoAcidHandler {
  private static aminoAacidArr: Array<{ oneLetter: string, threeLetters: string}> = [];

  public static getAminoAcidValue(param: string): string {
    let mappedParam: string;
    //Determine if it is a 1 letter or 3 letters amino acid representation (e.g. V600E or Val600Glu)
    let searchPatten: RegExp = new RegExp('^\\D{3}', 'i');
    let is3Letters : boolean = searchPatten.test(param); 
    let codeLength: number = is3Letters ? 3 : 1;
    
    //Handle GEN-40500 - if p change is 'Ser136=' the search parameter should be 'Ser136= | S136S'.
    let equalSign: Boolean = param.substring(param.length-1) == '=';
    let leftParam: string = this.getAminoAcidMappedValue(param.substring(0, codeLength), is3Letters);
    let rightParamLength: number = equalSign ? 1 : codeLength;
    let midParam: string = param.substring(codeLength, param.length - rightParamLength);
    let rightParam: string = equalSign ? leftParam : this.getAminoAcidMappedValue(param.substring(param.length - codeLength, param.length), is3Letters);
    mappedParam = leftParam + midParam + rightParam;
    return param + ' | ' + mappedParam; //Adding the mapped value to the original value with '|'
  }

  private static getAminoAcidMappedValue(param: string, is3Letters: boolean) {
    if (this.aminoAacidArr.length == 0) { // Filling mapping data once
      this.aminoAacidArr.push({ threeLetters: 'ala', oneLetter: 'A'});
      this.aminoAacidArr.push({ threeLetters: 'arg', oneLetter: 'R'});
      this.aminoAacidArr.push({ threeLetters: 'asn', oneLetter: 'N'});
      this.aminoAacidArr.push({ threeLetters: 'asp', oneLetter: 'D'});
      this.aminoAacidArr.push({ threeLetters: 'asx', oneLetter: 'B'});
      this.aminoAacidArr.push({ threeLetters: 'cys', oneLetter: 'C'});
      this.aminoAacidArr.push({ threeLetters: 'glu', oneLetter: 'E'});
      this.aminoAacidArr.push({ threeLetters: 'gln', oneLetter: 'Q'});
      this.aminoAacidArr.push({ threeLetters: 'glx', oneLetter: 'Z'});
      this.aminoAacidArr.push({ threeLetters: 'gly', oneLetter: 'G'});
      this.aminoAacidArr.push({ threeLetters: 'his', oneLetter: 'H'});
      this.aminoAacidArr.push({ threeLetters: 'ile', oneLetter: 'I'});
      this.aminoAacidArr.push({ threeLetters: 'leu', oneLetter: 'L'});
      this.aminoAacidArr.push({ threeLetters: 'lys', oneLetter: 'K'});
      this.aminoAacidArr.push({ threeLetters: 'glu', oneLetter: 'E'});
      this.aminoAacidArr.push({ threeLetters: 'met', oneLetter: 'M'});
      this.aminoAacidArr.push({ threeLetters: 'phe', oneLetter: 'F'});
      this.aminoAacidArr.push({ threeLetters: 'pro', oneLetter: 'P'});
      this.aminoAacidArr.push({ threeLetters: 'ser', oneLetter: 'S'});
      this.aminoAacidArr.push({ threeLetters: 'thr', oneLetter: 'T'});
      this.aminoAacidArr.push({ threeLetters: 'trp', oneLetter: 'W'});
      this.aminoAacidArr.push({ threeLetters: 'tyr', oneLetter: 'Y'});
      this.aminoAacidArr.push({ threeLetters: 'val', oneLetter: 'V'});
    }
    let outVal: string;
    const searchedField: string = is3Letters ? 'threeLetters' : 'oneLetter';
    const item = this.aminoAacidArr.find(item => item[searchedField].toUpperCase() == param.toUpperCase());
    if (item) {
      outVal = is3Letters ? item['oneLetter'] : item['threeLetters'];
    }
    return outVal;
  }
}
